<script setup lang="ts">
import type { Study, Teach } from '@pocketprep/types'
import { computed } from 'vue'
import { assignmentModule } from '@/store/assignment/module'
import { userModule } from '@/store/user/module'
import UIKit from '@pocketprep/ui-kit'

const {
    Icon,
} = UIKit

const props = defineProps<{
    assignment: Study.Class.PrebuiltQuizJSON
}>()

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const assignment = computed(() => props.assignment)

const assignmentName = computed(() => {
    return assignment.value?.name
})

const assignmentDueDateIso = computed(() => {
    return assignment.value?.dueDate?.iso
})

const formattedDueDate = computed(() => {
    if (assignmentDueDateIso.value) {
        return assignmentModule.getters.getAssignmentFormattedDate(assignmentDueDateIso.value)
    }

    return null
})

const hoursUntilAssignmentDue = computed(() => {
    return assignmentModule.getters.getHoursUntilAssignmentIsDue(assignment.value)
})

const isDueWithinTwentyFourHours = computed(() => {
    return hoursUntilAssignmentDue.value && hoursUntilAssignmentDue.value < 24
})

const isDueWithinTwentyFourHoursTimeString = computed(() => {
    if (isDueWithinTwentyFourHours.value && hoursUntilAssignmentDue.value) {
        const hoursUntilDue = hoursUntilAssignmentDue.value
        if (hoursUntilDue >= 1) {
            const hours = Math.floor(hoursUntilDue)
            return `in ${hours} ${hours > 1 ? 'hours' : 'hour'}`
        }

        const minutes = Math.floor(hoursUntilDue * 60)
        // example: if due in 20 seconds we still want to display due in 1 min
        return `in ${minutes > 1 ? minutes : 1} ${minutes > 1 ? 'mins' : 'min'}`
    }

    return null
})

const isAssignmentPastDue = computed(() => {
    return assignmentModule.getters.getIsAssignmentPastDue(assignmentDueDateIso.value)
})

const numOfQuestions = computed(() => {
    return assignment.value.questionSerials.length
})

const createdByName = computed(() => {
    const assignmentCreator = assignment.value.creator as Study.Class.UserJSON
    return assignmentCreator.firstName && assignmentCreator.lastName
        ? `${assignmentCreator.firstName} ${assignmentCreator.lastName}`
        : ''
})

const assignmentOrgName = computed(() => {
    const org = assignment.value.organization as Teach.Class.OrganizationJSON
    return org?.name
})
</script>

<template>
    <div
        v-dark 
        class="assignment-info"
        :class="{ 
            'assignment-info--no-due-date': !formattedDueDate
        }"
    >
        <div 
            v-dark 
            class="assignment-info__assignment-name"
            :class="{ 
                'assignment-info__assignment-name--no-due-date': !formattedDueDate
            }"
        >
            {{ assignmentName }}
        </div>
        <div
            v-if="formattedDueDate"
            v-dark 
            class="assignment-info__assignment-due-date"
            :class="{ 
                'assignment-info__assignment-due-date--today': isDueWithinTwentyFourHours && !isAssignmentPastDue
            }"
        >
            <Icon
                v-if="isDueWithinTwentyFourHours && !isAssignmentPastDue"
                v-dark
                class="assignment-info__warning-icon"
                type="warning"
                :is-dark-mode="isDarkMode"
            />
            Due {{ isDueWithinTwentyFourHours && !isAssignmentPastDue ? 
                isDueWithinTwentyFourHoursTimeString : formattedDueDate }}
        </div>
        <div v-dark class="assignment-info__assignment-info-bottom-half">
            <div v-dark class="assignment-info__assignment-num-of-questions">
                {{ numOfQuestions }} Questions
            </div>
            <div
                v-if="createdByName || assignmentOrgName"
                v-dark 
                class="assignment-info__assignment-created-by"
            >
                Created by {{ createdByName ? `${createdByName} at` : '' }} {{ assignmentOrgName }}
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.assignment-info {
    position: relative;
    display: flex;
    width: 482px;
    padding: 15px;
    flex-direction: column;
    align-items: flex-start;
    border-radius: 5px;
    border: 1px solid $banana-bread;
    background: $white;

    &::after {
        content: '';
        border-radius: 8px;
        background: url('@/assets/assignments/halftone-yellow-lm.png') repeat;
        background-size: 85px;
        position: absolute;
        width: 514px;
        height: 156px;
        top: 6px;
        left: 6px;
        z-index: -1;
    }

    @include breakpoint(black-bear) {
        width: 320px;

        &::after {
            content: '';
            border-radius: 8px;
            background: url('@/assets/assignments/halftone-yellow-lm.png') repeat;
            background-size: 85px;
            position: absolute;
            width: 352px;
            height: 174px;
            top: 6px;
            left: 6px;
            z-index: -1;
        }
    }

    &--dark {
        border: 1px solid $ash;
        background: $brand-black;

        &::after {
            content: '';
            border-radius: 8px;
            background: url('@/assets/assignments/halftone-yellow-dm.png') repeat;
            background-size: 85px;
            position: absolute;
            width: 514px;
            height: 156px;
            top: 6px;
            left: 6px;
            z-index: -1;
        }

        @include breakpoint(black-bear) {
            width: 320px;

            &::after {
                content: '';
                border-radius: 8px;
                background: url('@/assets/assignments/halftone-yellow-dm.png') repeat;
                background-size: 85px;
                position: absolute;
                width: 352px;
                height: 174px;
                top: 6px;
                left: 6px;
                z-index: -1;
            }
        }
    }

    &--no-due-date {
        &::after {
            height: 128px;
        }

        @include breakpoint(black-bear) {
            &::after {
                height: 148px;
            }
        }
    }


    &__assignment-name {
        position: relative;
        margin-bottom: 4px;
        color: $brand-black;
        font-size: 18px;
        font-weight: 600;
        line-height: 25px;

        &--dark {
            color: $white;
        }

        &--no-due-date {
            margin-bottom: 12px;   
        }
    }

    &__assignment-due-date {
        display: flex;
        align-items: center;
        color: $slate;
        font-size: 16px;
        font-weight: 500;
        line-height: 22px;
        margin-bottom: 12px;

        &--dark {
            color: $fog;
        }

        &--today {
            color: $pepper;

            &--dark {
                color: $rosa;
            }
        }
    }

    &__warning-icon {
        margin-right: 4px;
        margin-bottom: 4px;
    }

    &__assignment-info-bottom-half {
        width: 100%;
        border-top: 1px solid $fog;

        &--dark {
            border-top: 1px solid $ash;
        }
    }

    &__assignment-num-of-questions {
        margin-top: 12px;
        margin-bottom: 4px;
        color: $brand-black;
        font-size: 16px;
        font-weight: 600;
        line-height: 22px;

        &--dark {
            color: $white;
        }
    }

    &__assignment-created-by {
        color: $slate;
        font-size: 15px;
        font-weight: 500;
        line-height: 20px;

        &--dark {
            color: $fog;
        }
    }
}
</style>
